import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2,
  Loader2,
  XCircle,
  Clock,
  ExternalLink,
  Download,
  Copy,
} from "lucide-react";
import { motion } from "framer-motion";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import API from "../lib/api";

export default function BuildDetails() {
  const { id } = useParams();

  const [build, setBuild] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ FETCH + POLLING
  useEffect(() => {
    const fetchBuild = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await API.get(`/builds/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setBuild(res.data.build);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBuild();

    const interval = setInterval(fetchBuild, 2000);
    return () => clearInterval(interval);
  }, [id]);

  // ✅ COPY
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // ✅ STATUS UI
  const getStatusBadge = () => {
    if (!build) return null;

    if (build.status === "completed") {
      return (
        <div className="bg-green-500/10 text-green-400 px-4 py-2 rounded-full flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Completed
        </div>
      );
    }

    if (build.status === "failed") {
      return (
        <div className="bg-red-500/10 text-red-400 px-4 py-2 rounded-full flex items-center gap-2">
          <XCircle className="w-4 h-4" /> Failed
        </div>
      );
    }

    return (
      <div className="bg-purple-500/10 text-purple-400 px-4 py-2 rounded-full flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> In Progress
      </div>
    );
  };

  // ✅ LOADING UI
  if (loading || !build) {
    return (
      <div className="dark min-h-screen flex items-center justify-center text-white">
        <Loader2 className="animate-spin mr-2" /> Loading build...
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-background text-white">
      <Sidebar />

      <div className="md:ml-64">
        <TopBar />

        <main className="p-6 md:p-12 max-w-6xl mx-auto space-y-8">

          {/* HEADER */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <h1 className="text-4xl font-semibold">Build Details</h1>
            {getStatusBadge()}
          </motion.div>

          {/* CARD */}
          <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-6">

            {/* PROMPT */}
            <div>
              <p className="text-muted-foreground mb-1">Prompt</p>
              <h2 className="text-xl font-medium">{build.prompt}</h2>
            </div>

            {/* PROGRESS */}
            <div>
              <div className="flex justify-between mb-2 text-sm">
                <span>Progress</span>
                <span>{build.progress}%</span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full">
                <div
                  className="h-2 bg-purple-500 rounded-full transition-all"
                  style={{ width: `${build.progress}%` }}
                />
              </div>
            </div>

            {/* INFO GRID */}
            <div className="grid md:grid-cols-3 gap-4">
              <InfoCard icon={<Clock />} label="Created" value={new Date(build.created_at).toLocaleString()} />
              <InfoCard icon={<Clock />} label="Duration" value={`${build.build_time_seconds || 0}s`} />
              <InfoCard icon={<CheckCircle2 />} label="Status" value={build.status} />
            </div>

          </div>

          {/* STEPS */}
          {/* <Steps progress={build.progress} /> */}

          {/* PREVIEW */}
          {build.status === "completed" && (
            <PreviewSection build={build} copy={copyToClipboard} />
          )}

          {/* DOWNLOADS */}
          {build.status === "completed" && (
            <DownloadSection />
          )}

          {/* ERROR */}
          {build.status === "failed" && (
            <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400">{build.error_message}</p>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="p-4 bg-black/40 rounded-xl border border-white/10">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {icon} {label}
      </div>
      <div className="text-white">{value}</div>
    </div>
  );
}



// 🌐 PREVIEW
function PreviewSection({ build, copy }) {
  const previews = [
    {
      id: 1,
      title: "Website Preview",
      description: "Live frontend application preview",
      url: build.website_preview_url,
      thumbnail:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f"
    },
    {
      id: 2,
      title: "Admin Panel",
      description: "Dashboard & backend management",
      url: build.admin_preview_url,
      thumbnail:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
    }
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-1">
          Live Previews
        </h2>
        <p className="text-muted-foreground">
          View your generated applications
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {previews.map((preview) => (
          <motion.div
            key={preview.id}
            whileHover={{ y: -5 }}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 hover:border-purple-500/30 transition-all duration-500"
          >
            <div className="aspect-video relative overflow-hidden">
              <img
                src={preview.thumbnail}
                alt={preview.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center backdrop-blur-sm">
                <motion.a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold shadow-lg"
                >
                  <ExternalLink className="w-5 h-5" />
                  Open Preview
                </motion.a>
              </div>
            </div>

            <div className="p-6 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    {preview.title}
                  </h3>

                  <p className="text-sm text-muted-foreground">
                    {preview.description}
                  </p>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20 text-xs font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Ready
                </div>
              </div>

              <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {preview.url}
                </p>

                <button
                  onClick={() => copy(preview.url)}
                  className="text-muted-foreground hover:text-white transition"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// 📥 DOWNLOADS
function DownloadSection() {
  const downloads = [

    {
      id: 1,
      title: "Website Source Code",
      description: "Complete React + Node.js codebase",
      format: "ZIP",
      size: "12.5 MB"
    },

    {
      id: 2,
      title: "Admin Panel Source Code",
      description: "Full-featured admin dashboard",
      format: "ZIP",
      size: "8.3 MB"
    },

    {
      id: 3,
      title: "Android APK",
      description: "Ready to install on devices",
      format: "APK",
      size: "45.2 MB"
    },

    {
      id: 4,
      title: "Android AAB",
      description: "Google Play Store bundle",
      format: "AAB",
      size: "38.7 MB"
    },

    {
      id: 5,
      title: "iOS IPA",
      description: "iOS app package for TestFlight",
      format: "IPA",
      size: "52.1 MB"
    }

  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-1">
          Downloads
        </h2>

        <p className="text-muted-foreground">
          Get your generated application files
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {downloads.map((item) => (
          <motion.div
            key={item.id}
            whileHover={{ y: -5 }}
            className="flex flex-col p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-purple-500/30 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Download className="w-6 h-6 text-purple-400" />
              </div>

              <div className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-xs font-medium text-muted-foreground">
                {item.format}
              </div>
            </div>

            <div className="flex-1 space-y-2 mb-4">
              <h3 className="text-lg font-semibold text-white">
                {item.title}
              </h3>

              <p className="text-sm text-muted-foreground">
                {item.description}
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <span className="text-sm text-muted-foreground">
                {item.size}
              </span>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}